/**
 * common CarouselMobile component used by other mudules as a reusable component which returns ui for gallery carousel component. required fields can be passed as props
 *
 * @module views/components/CarouselMobile
 * @memberof -Common
 */
import './CarouselMobile.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Image from '@ulta/core/components/Image/Image';
import { Link_Huge } from '@ulta/core/components/Link_Huge/Link_Huge';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import Video from '@ulta/core/components/Video/Video';
import { hasItems } from '@ulta/core/utils/array/array';

import Tag from '@ulta/modules/Tag/Tag';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

/**
 * Represents a MediaGallery component for mobile
 *
 * @method
 * @param {CarouselMobileProps} props - React properties passed from composition
 * @returns CarouselMobile
 */
const CarouselMobile = ( props ) => {
  const {
    badges,
    componentKey,
    items,
    imageWidth,
    ratio,
    tryItAction,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextClickAction,
    previousClickAction,
    nextAccessibility = defaultProps.nextAccessibility,
    previousAccessibility = defaultProps.previousAccessibility
  } = props;

  const carouselRef = useRef();
  const isSingleItem = items.length === 1;

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  const _railMeta = {
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  const _railADAMeta = {
    carouselAccessibility: carouselUtils.handleTokenizedString( carouselAccessibility ),
    carouselDetailsAccessibility: carouselUtils.handleTokenizedString( carouselDetailsAccessibility )
  };

  const ItemGroup = items?.length && items.map( ( item, index ) => {
    if( item.metaData?.mediaType === 'img' ){
      return (
        <Slide key={ index }>
          <div className={
            classNames( 'CarouselMobile--Image', {
              'CarouselMobile--SingleImage': isSingleItem
            }
            ) }
          >
            <Image
              src={ item?.imageUrl }
              alt={ item?.altText }
              key={ index }
              width={ imageWidth }
              aspectRatio={ ratio }
              metaData={ item?.metaData }
              ariaHidden={ item?.ariaHidden }
              ariaLabel={ carouselUtils.handleTokenizedString( carouselCardAccessibility, [index + 1, items.length] ) }
            />
          </div>
        </Slide>
      );
    }
    else {
      return (
        <Slide key={ index }>
          <div className={
            classNames( 'CarouselMobile--Video', {
              'CarouselMobile--SingleVideo': isSingleItem
            } ) }
          >
            <Video
              imageUrl={ item?.imageUrl }
              videoUrl={ item?.videoUrl }
              alt={ item?.altText }
              closedCaptionUrl={ item.closedCaptionUrl }
              metaData={ item?.metaData }
              ariaLabel={ carouselUtils.handleTokenizedString( carouselCardAccessibility, [index + 1, items.length] ) }
              isCaptionsEnabled={ item.isCaptionsEnabled }
              videoProfiles={ item.videoProfiles }
              preloadNone={ 'none' }
            />
          </div>
        </Slide>
      );
    }
  } );
  return (
    <div className='CarouselMobile'
      role='region'
      aria-label={ carouselAccessibility }
    >
      { hasItems( badges ) && badges.map( ( badge, index )=>
        <div
          key={ `${componentKey}-CarouselMobile-badge-${index}` }
          className='CarouselMobile__badge'
        >
          <Tag
            backgroundColor={ badge.backgroundColor }
            icon={ badge.icon }
            message={ badge.message }
            textColor={ badge.textColor }
            variant={ badge.variant }
          />
        </div>
      ) }
      { ItemGroup.length > 1 ? (
        <>
          <Rail
            ref={ carouselRef }
            id={ railUuid }
            items={ items }
            previousClickAction={ previousClickAction }
            nextClickAction={ nextClickAction }
            useFreeMode={ true }
            _railMeta={ _railMeta }
            _railADAmeta={ _railADAMeta }
            showBullets={ true }
            sliderPerViewMD={ 1.5 }
            slidesPerGroupMD={ 1 }
            sliderPerViewSM={ 1.2 }
            slidesPerGroupSM={ 1 }
          >
            { ItemGroup }
          </Rail>
        </>
      ) :
        ItemGroup[0]
      }
      { tryItAction?.url && tryItAction?.label &&
      <Link_Huge
        compact={ true }
        action={ tryItAction }
      >
        <Icon
          size='s'
          name={ 'Tryon' }
        />
        { tryItAction.label }
      </Link_Huge>
      }
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
 */
export const defaultProps = {
  previousAccessibility: '',
  nextAccessibility: ''
};


/**
  * Property type definitions
  * @typedef CarouselMobileProps
  * @type {object}
  * @property {array} items - Sets the items as images and videos.
  * @property {number} imageWidth - Sets the width of image
  * @property {string} ratio - Sets the ratio of image
  * @property {object} tryItAction - Set the try it link and label
  * @property {string} carouselAccessibility - Sets the carousel accessibility
  * @property {string} carouselDetailsAccessibility - Sets the carousel details accessibility
  * @property {string} carouselCardAccessibility - Sets the carousel card accessibility
  * @property {array} badges - shows badges
  */
export const propTypes = {
  /**
   * The elements to display as slides in the carousel
   */
  items: PropTypes.array.isRequired,
  /**
   * The width of image
   */
  imageWidth: PropTypes.number,
  /**
   * The ratio of image
   */
  ratio: PropTypes.string,
  /**
   * The prop that hold the label and link url for try it.
   */
  tryItAction: PropTypes.object,
  /**
   * The prop that hold the carousel accessibility
   */
  carouselAccessibility: PropTypes.string,
  /**
   * The prop that hold the carousel details accessibility.
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * The prop that holds carousel card accessibility
   */
  carouselCardAccessibility: PropTypes.string,
  /**
    *  prop that shows badges.
    */
  badges: PropTypes.array
};

CarouselMobile.propTypes = propTypes;

export default CarouselMobile;